import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useCancelToken } from 'utils/hooks/useCancelToken'
import { Button, Grid, Icon, List, Spinner } from 'gu-ui-kit'
import styles from 'modules/Agency/agency.module.scss'
import FilterPanel from 'components/Filters/FilterPanel'
import EmptyResults from 'components/EmptyResults'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import config from 'modules/Agency/Requests/filters'
import { useTranslation } from 'react-i18next'
import { useTableConfig } from 'modules/Agency/Requests/subcomponents/hooks/useTableConfig'
import { Link } from 'react-router-dom'
import { ROUTES } from 'constants/routes'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import GridSpinner from 'components/UI/GridSpinner'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import PageHeader from 'components/PageHeader'

const Requests = () => {
  const breakpoints = useBreakpoint()
  const dispatch = useDispatch()
  const { cancelableDispatch } = useCancelToken()
  const { t } = useTranslation()
  const pathGen = useGeneratePathI18n()
  const [page, setPage] = useState(1)

  const { loading, data, filters, pagination } = useSelector(
    (state) => state['agency/requests'],
  )

  const handleChangePage = useCallback(() => {
    setPage(page + 1)
  }, [setPage, page])

  const filterManager = useFiltersManager({
    filters: filters,
    config: config(),
    setAction: 'AGENCY/REQUESTS_SET_FILTER',
    localStorageKey: 'agency/requests',
  })

  useEffect(() => {
    dispatch({ type: 'AGENCY/REQUESTS_DROP_DATA' })
    setPage(1)
  }, [filterManager.params])

  useEffect(() => {
    filterManager.params &&
      cancelableDispatch({
        type: 'AGENCY/REQUESTS_FETCH',
        payload: {
          params: {
            ...filterManager.params,
            offset: pagination.limit * (page - 1),
            limit: pagination.limit,
          },
        },
      })
  }, [filterManager.params, page])

  const handleDeleteRequest = useCallback((id) => {
    dispatch({
      type: 'AGENCY/REQUESTS_DELETE_REQUEST_CONFIRM',
      payload: {
        id,
        text: t('agency::requests_list.confirm_delete'),
      },
    })
  }, [])

  const { tableState, tableConfig } = useTableConfig(
    {
      deleteRequest: handleDeleteRequest,
    },
    data,
  )

  return (
    <>
      <Grid
        justifyContent={'between'}
        className={breakpoints.fb ? 'margin-bottom-16' : 'margin-bottom-32'}
        alignItems={breakpoints.sm ? 'start' : 'center'}
        direction={breakpoints.sm ? 'column' : 'row'}
        gap={16}
      >
        <PageHeader>{t('agency::navigation.requests')}</PageHeader>
        <Button
          fullWidth={breakpoints.sm}
          size={'m'}
          component={Link}
          to={pathGen(ROUTES.AGENCY_ADD_CLIENT)}
          variant={'contained'}
          startIcon={<Icon name={'plus'} />}
        >
          {t('agency::add_client')}
        </Button>
      </Grid>
      <FilterPanel
        filterManager={filterManager}
        className={styles['filter-panel']}
      />

      <List
        table={tableState}
        config={tableConfig}
        EmptyComponent={() =>
          !loading && data.length === 0 ? (
            <EmptyResults
              header={t('agency::empty_list.requests.heading')}
              text={
                <Button
                  size={'m'}
                  component={Link}
                  to={pathGen(ROUTES.AGENCY_ADD_CLIENT)}
                  variant={'contained'}
                  startIcon={<Icon name={'plus'} />}
                >
                  {t('agency::add_client')}
                </Button>
              }
              className={styles['agency-list__empty-results']}
            />
          ) : (
            <GridSpinner />
          )
        }
      />
      {pagination.total > data.length && (
        <div className={styles['pagination']}>
          <Grid
            alignItems={'center'}
            justifyContent={'center'}
            className={styles['show-more__container']}
          >
            <Button
              onClick={handleChangePage}
              className={styles['show-more__button']}
              variant={'contained'}
              startIcon={
                loading ? (
                  <Spinner width={16} color={'inherit'} />
                ) : (
                  <Icon name={'plus'} size={'s'} />
                )
              }
            >
              {t('common::pagination.show_more')}
            </Button>
          </Grid>
        </div>
      )}
    </>
  )
}

export default Requests
